import React, { useState, useEffect, useRef } from "react";
import { useNavigate, Link } from "react-router-dom";
import { Icon } from "@iconify/react";
import AOS from "aos";
import "aos/dist/aos.css";
import "./collection.css";

export default function Collections({
	data = [],
	years = [],
	categories = [],
}) {
	const [bridal, setBridal] = useState([]);
	const [prom, setProm] = useState([]);
	const [evening, setEvening] = useState([]);
	const navigate = useNavigate();
	const scrollRef1 = useRef(null);
	const scrollRef2 = useRef(null);
	const scrollRef3 = useRef(null);

	console.log("Collections component received data:", data);

	useEffect(() => {
		if (data && data.length > 0 && years && years.length > 0) {
			// Sort years in descending order (newest first)
			const sortedYears = [...years].sort((a, b) => b - a);

			// Function to get products for a category across years (prioritizing newer years)
			const getProductsByCategory = (categoryKeywords, maxProducts = 10) => {
				const products = [];

				// Iterate through years from highest to lowest
				for (const year of sortedYears) {
					if (products.length >= maxProducts) break;

					// First try to get products with availability "yes"
					const availableProducts = data.filter(
						(item) =>
							item.viewInfront === true &&
							item.availability?.toLowerCase() === "yes" &&
							item.year === year &&
							item.category &&
							categoryKeywords.some((keyword) =>
								item.category.toLowerCase().includes(keyword.toLowerCase())
							)
					);

					// Add available products first
					for (const product of availableProducts) {
						if (products.length >= maxProducts) break;
						products.push(product);
					}

					// If still need more products, get ones with viewInfront true (regardless of availability)
					if (products.length < maxProducts) {
						const viewInfrontProducts = data.filter(
							(item) =>
								item.viewInfront === true &&
								item.year === year &&
								item.category &&
								categoryKeywords.some((keyword) =>
									item.category.toLowerCase().includes(keyword.toLowerCase())
								) &&
								// Exclude products already added
								!products.some((p) => p._id === item._id)
						);

						for (const product of viewInfrontProducts) {
							if (products.length >= maxProducts) break;
							products.push(product);
						}
					}
				}

				return products;
			};

			// Get products for each category
			const bridalData = getProductsByCategory(["Enchanted Bridal"], 10);
			const promData = getProductsByCategory(["Prom"], 5);
			const eveningData = getProductsByCategory(["Enchanted Evening"], 5);

			setBridal(bridalData);
			setProm(promData);
			setEvening(eveningData);
		}
	}, [data, years, categories]);

	useEffect(() => {
		AOS.init({
			duration: 500,
			easing: "ease-in-out",
			once: true,
		});
	}, []);

	// Scroll functions
	const scrollLeft = (ref) => {
		if (ref.current) {
			ref.current.scrollBy({ left: -360, behavior: "smooth" });
		}
	};

	const scrollRight = (ref) => {
		if (ref.current) {
			ref.current.scrollBy({ left: 360, behavior: "smooth" });
		}
	};

	// Get highest year for display and navigation
	const highestYear = years && years.length > 0 ? Math.max(...years) : 2026;

	// Function to get display year text for a collection
	const getCollectionYearText = (collection, baseText) => {
		if (collection.length === 0) return baseText;

		// Get unique years from the collection
		const uniqueYears = [...new Set(collection.map((item) => item.year))].sort(
			(a, b) => b - a
		);

		if (uniqueYears.length === 1) {
			return `${uniqueYears[0]} ${baseText}`;
		} else {
			return `${uniqueYears.join(" & ")} ${baseText}`;
		}
	};

	return (
		<div className="mb-20 px-4 md:px-10">
			{/* Section Title */}
			<div className="text-center font-lora font-semibold text-3xl md:text-5xl mb-20">
				<h3>Our Popular Collections</h3>
			</div>

			{/* -------- Section 1: Prom -------- */}
			{prom.length > 0 && (
				<div className="w-full flex flex-col md:flex-row items-start gap-10">
					<div
						className="md:w-1/3 w-full lg:min-h-[400px] h-auto flex justify-center items-center rounded-xl"
						data-aos="fade-right">
						<div className="space-y-6 h-fit">
							<h2 className="font-lora text-2xl md:text-3xl font-bold">
								{getCollectionYearText(prom, "Prom Gowns")}
							</h2>
							<p className="font-poppins text-gray-600 text-base md:text-lg">
								Go to Prom in Style with our latest{" "}
								{getCollectionYearText(prom, "Prom Gowns").toLowerCase()}{" "}
								featuring dazzling dress designs.
							</p>
							<button
								onClick={() => navigate(`/prom/${highestYear}`)}
								className="px-6 py-3 font-lora bg-black text-white text-base md:text-lg font-semibold rounded-full hover:bg-gray-900 transition">
								Explore Collection
							</button>
						</div>
					</div>

					<div className="md:w-2/3 w-full relative">
						<div
							ref={scrollRef1}
							className="flex gap-4 overflow-x-auto scroll-smooth no-scrollbar">
							{prom.map((item, index) => (
								<Link
									to={`/product/id/${item.style}`}
									state={{
										product: item,
									}}
									key={`${item.style}-${item.year}-${index}`}
									className="relative group flex-shrink-0 w-[250px] sm:w-[300px] md:w-[350px] h-[400px] sm:h-[400px] md:h-[450px]">
									{item.images && item.images[0] && (
										<img
											src={
												item.images[0].startsWith("http")
													? item.images[0]
													: `https://admin.monsinidress.com/${item.images[0]}`
											}
											alt={`Dress ${item.style}`}
											className="w-full h-full object-cover rounded-xl shadow-md"
										/>
									)}

									<p className="flex justify-center items-center font-poppins text-[22px] absolute bottom-4 right-2 text-white text-sm md:text-base px-3 py-1 rounded opacity-0 group-hover:opacity-100 transition-opacity duration-500">
										{item.style}
										<Icon
											icon="flowbite:arrow-right-outline"
											width="1.5rem"
											height="1.5rem"
											className="ms-5 hidden md:block lg:block"
											style={{ color: "white" }}
										/>
									</p>
								</Link>
							))}
						</div>

						{/* Arrows */}
						<button
							onClick={() => scrollLeft(scrollRef1)}
							className="absolute left-0 top-1/2 -translate-y-1/2 bg-black text-white p-3 rounded-full shadow hover:bg-gray-800 hidden sm:flex">
							&#8592;
						</button>
						<button
							onClick={() => scrollRight(scrollRef1)}
							className="absolute right-0 top-1/2 -translate-y-1/2 bg-black text-white p-3 rounded-full shadow hover:bg-gray-800 hidden sm:flex">
							&#8594;
						</button>
					</div>
				</div>
			)}

			{/* -------- Section 2: Evening -------- */}
			{evening.length > 0 && (
				<div className="w-full flex flex-col md:flex-row items-start gap-10 mt-20">
					<div className="md:w-2/3 w-full relative">
						<div
							ref={scrollRef2}
							className="flex gap-4 overflow-x-auto scroll-smooth no-scrollbar">
							{evening.map((item, index) => (
								<Link
									to={`/product/id/${item.style}`}
									state={{
										product: item,
									}}
									key={`${item.style}-${item.year}-${index}`}
									className="relative group flex-shrink-0 w-[250px] sm:w-[300px] md:w-[350px] h-[400px] sm:h-[400px] md:h-[450px]">
									{item.images && item.images[0] && (
										<img
											src={
												item.images[0].startsWith("http")
													? item.images[0]
													: `https://admin.monsinidress.com/${item.images[0]}`
											}
											alt={`Dress ${item.style}`}
											className="w-full h-full object-cover rounded-xl shadow-md"
										/>
									)}

									<p className="flex justify-center items-center font-poppins text-[22px] absolute bottom-4 right-2 text-white text-sm md:text-base px-3 py-1 rounded opacity-0 group-hover:opacity-100 transition-opacity duration-500">
										{item.style}
										<Icon
											icon="flowbite:arrow-right-outline"
											width="1.5rem"
											height="1.5rem"
											className="ms-5 hidden md:block lg:block"
											style={{ color: "white" }}
										/>
									</p>
								</Link>
							))}
						</div>

						{/* Arrows */}
						<button
							onClick={() => scrollLeft(scrollRef2)}
							className="absolute left-0 top-1/2 -translate-y-1/2 bg-black text-white p-3 rounded-full shadow hover:bg-gray-800 hidden sm:flex">
							&#8592;
						</button>
						<button
							onClick={() => scrollRight(scrollRef2)}
							className="absolute right-0 top-1/2 -translate-y-1/2 bg-black text-white p-3 rounded-full shadow hover:bg-gray-800 hidden sm:flex">
							&#8594;
						</button>
					</div>

					<div
						className="md:w-1/3 w-full lg:min-h-[400px] h-auto flex justify-center items-center rounded-xl"
						data-aos="fade-right">
						<div className="space-y-6 h-fit">
							<h2 className="font-lora text-2xl md:text-3xl font-bold">
								{getCollectionYearText(evening, "Enchanted Evening Gowns")}
							</h2>
							<p className="font-poppins text-gray-600 text-base md:text-lg">
								Step into elegance with our beautiful evening dress collection.
							</p>
							<button
								onClick={() => navigate(`/evening/${highestYear}`)}
								className="px-6 py-3 font-lora bg-black text-white text-base md:text-lg font-semibold rounded-full hover:bg-gray-900 transition">
								Explore Collection
							</button>
						</div>
					</div>
				</div>
			)}

			{/* -------- Section 3: Bridal -------- */}
			{bridal.length > 0 && (
				<div className="w-full flex flex-col items-start gap-10 mt-20">
					<div
						className="w-full h-auto flex justify-center items-center rounded-xl"
						data-aos="fade-right">
						<div className="space-y-6 h-fit text-center">
							<h2 className="font-lora text-2xl md:text-3xl font-bold">
								{getCollectionYearText(bridal, "Enchanted Bridal Gowns")}
							</h2>
							<p className="font-poppins text-gray-600 text-base md:text-lg">
								Find the perfect wedding gown that makes your special day
								unforgettable.
							</p>
							<button
								onClick={() => navigate(`/bridal/${highestYear}`)}
								className="px-6 py-3 font-lora bg-black text-white text-base md:text-lg font-semibold rounded-full hover:bg-gray-900 transition">
								Explore Collection
							</button>
						</div>
					</div>

					<div className="w-full relative">
						<div
							ref={scrollRef3}
							className="flex gap-4 overflow-x-auto scroll-smooth no-scrollbar">
							{bridal.map((item, index) => (
								<Link
									to={`/product/id/${item.style}`}
									state={{
										product: item,
									}}
									key={`${item.style}-${item.year}-${index}`}
									className="relative group flex-shrink-0 w-[250px] sm:w-[300px] md:w-[350px] h-[400px] sm:h-[400px] md:h-[450px]">
									{item.images && item.images[0] && (
										<img
											src={
												item.images[0].startsWith("http")
													? item.images[0]
													: `https://admin.monsinidress.com/${item.images[0]}`
											}
											alt={`Dress ${item.style}`}
											className="w-full h-full object-cover rounded-xl shadow-md"
										/>
									)}

									<p className="flex justify-center items-center font-poppins text-[22px] absolute bottom-4 right-2 text-white text-sm md:text-base px-3 py-1 rounded opacity-0 group-hover:opacity-100 transition-opacity duration-500">
										{item.style}
										<Icon
											icon="flowbite:arrow-right-outline"
											width="1.5rem"
											height="1.5rem"
											className="ms-5 hidden md:block lg:block"
											style={{ color: "white" }}
										/>
									</p>
								</Link>
							))}
						</div>

						{/* Arrows */}
						<button
							onClick={() => scrollLeft(scrollRef3)}
							className="absolute left-0 top-1/2 -translate-y-1/2 bg-black text-white p-3 rounded-full shadow hover:bg-gray-800 hidden sm:flex">
							&#8592;
						</button>
						<button
							onClick={() => scrollRight(scrollRef3)}
							className="absolute right-0 top-1/2 -translate-y-1/2 bg-black text-white p-3 rounded-full shadow hover:bg-gray-800 hidden sm:flex">
							&#8594;
						</button>
					</div>
				</div>
			)}
		</div>
	);
}
